import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Localization from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';

import en from './en.json';
import nl from './nl.json';

const LANGUAGE_STORAGE_KEY = 'user_language';

const languageDetector = {
  type: 'languageDetector' as const,
  async: true,
  detect: async (callback: (lang: string) => void) => {
    try {
      const savedLanguage = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLanguage) {
        callback(savedLanguage);
        return;
      }

      // Priority: environment default > device locale (if supported) > 'en'
      const defaultLanguage = process.env.EXPO_PUBLIC_DEFAULT_LANGUAGE || 'nl';
      const supportedLanguages = ['en', 'nl'];
      
      // Use environment default if it's a supported language
      if (supportedLanguages.includes(defaultLanguage)) {
        callback(defaultLanguage);
        return;
      }
      
      // Fallback to device locale if supported
      const locales = Localization.getLocales();
      const deviceLocale = locales[0]?.languageCode || 'en';
      const finalLanguage = supportedLanguages.includes(deviceLocale) 
        ? deviceLocale 
        : 'en';
      
      callback(finalLanguage);
    } catch (error) {
      console.warn('Language detection failed, defaulting to environment default or English', error);
      const defaultLanguage = process.env.EXPO_PUBLIC_DEFAULT_LANGUAGE || 'en';
      callback(['en', 'nl'].includes(defaultLanguage) ? defaultLanguage : 'en');
    }
  },
  init: () => {},
  cacheUserLanguage: async (language: string) => {
    try {
      await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch (error) {
      console.warn('Failed to save language preference', error);
    }
  },
};

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      nl: { translation: nl },
    },
    fallbackLng: process.env.EXPO_PUBLIC_DEFAULT_LANGUAGE || 'nl',
    interpolation: {
      escapeValue: false, // React Native already escapes
    },
    react: {
      useSuspense: false, // Important for React Native
      bindI18n: 'languageChanged', // Ensure components re-render on language change
      bindI18nStore: '', // Don't bind to store changes
    },
    // Log missing translations in development
    saveMissing: __DEV__,
    missingKeyHandler: (lng, ns, key) => {
      if (__DEV__) {
        console.warn(`Missing translation: [${lng}] ${key}`);
      }
    },
  });

export default i18n;
